import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ZoomIn } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Card, CardContent } from '@ooh/ui/components/card';
import { DetailPhotoLightbox } from './detail-photo-lightbox';
import type { DetailViewUnit } from './detail-view-unit';

// Spaced 200 ms apart so browsers do not throttle rapid programmatic anchor clicks within one user-gesture frame.
const DOWNLOAD_STAGGER_MS = 200;

type Photo = NonNullable<DetailViewUnit['photos']>[number];

function downloadAll(photos: Photo[]) {
  photos.forEach((photo, index) => {
    setTimeout(() => {
      const a = document.createElement('a');
      a.href = photo.url;
      a.download = photo.originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }, index * DOWNLOAD_STAGGER_MS);
  });
}

export function DetailGallerySection({ unit }: { unit: DetailViewUnit }) {
  const { t } = useTranslation('inventory');
  const [activeThumb, setActiveThumb] = React.useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);
  const photos = unit.photos ?? [];
  const activePhoto = photos[activeThumb] ?? null;

  React.useEffect(() => {
    setActiveThumb(0);
  }, [unit.id]);

  return (
    <>
      <Card data-testid="photo-gallery">
        <CardContent className="p-4">
          <div className="relative aspect-video rounded-lg overflow-hidden bg-muted mb-3">
            {photos.length > 0 ? (
              <>
                <img
                  src={photos[activeThumb]?.displayUrl}
                  alt={`Zdjecie nosnika ${unit.displayNumber}`}
                  className="w-full h-full object-cover"
                  data-testid="gallery-hero"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setIsLightboxOpen(true)}
                  aria-label={t('detail.openLightbox')}
                  data-testid="gallery-lightbox-trigger"
                  className="absolute right-2 top-2 h-8 w-8 rounded-full bg-black/45 text-white hover:bg-black/60 hover:text-white"
                >
                  <ZoomIn className="h-4 w-4" strokeWidth={2} />
                </Button>
              </>
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                data-testid="gallery-hero"
              >
                <span className="text-[13px] text-muted-foreground">{t('detail.noPhotos')}</span>
              </div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {photos.map((photo, i) => (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => setActiveThumb(i)}
                  className={`rounded-md overflow-hidden border-2 p-0 h-auto transition-colors flex-shrink-0 ${
                    activeThumb === i ? 'border-primary' : 'border-transparent hover:border-border'
                  }`}
                  data-testid={`gallery-thumb-${i}`}
                  aria-label={`Zdjecie ${i + 1}`}
                >
                  <img
                    src={photo.thumbnailUrl}
                    alt={`Miniatura ${i + 1}`}
                    loading="lazy"
                    className="w-[90px] h-[60px] object-cover"
                  />
                </button>
              ))}
            </div>
          )}
          {activePhoto !== null && (
            <div className="flex gap-2 mt-3">
              <Button asChild variant="outline" size="sm" data-testid="gallery-download-single">
                <a href={activePhoto.url} download={activePhoto.originalName}>
                  <Download className="h-3.5 w-3.5" />
                  {t('detail.downloadPhoto')}
                </a>
              </Button>
              {photos.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="gallery-download-all"
                  onClick={() => downloadAll(photos)}
                >
                  <Download className="h-3.5 w-3.5" />
                  {t('detail.downloadAllPhotos')}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {isLightboxOpen && photos.length > 0 ? (
        <DetailPhotoLightbox
          photos={photos}
          activeIndex={activeThumb}
          onClose={() => setIsLightboxOpen(false)}
          onNavigate={setActiveThumb}
          labels={{
            close: t('detail.lightbox.close'),
            previous: t('detail.lightbox.previous'),
            next: t('detail.lightbox.next'),
            counter: (current, total) => t('detail.lightbox.counter', { current, total }),
          }}
        />
      ) : null}
    </>
  );
}
